/**
 * Email campaign documents: a campaign groups related `EmailSendJob`s so that
 * a recipient reached by one send is not reached again by a later send in the
 * same campaign.
 *
 * Two document types live here:
 *
 * - **`EmailCampaignDocument`** (`email-campaigns/{campaignId}`) — the campaign
 *   itself: its name, the course it belongs to, and rollup counters for the
 *   admin view.
 * - **`EmailCampaignRecipientDocument`**
 *   (`email-campaigns/{campaignId}/recipients/{recipientId}`) — one row per
 *   address the campaign has already claimed. This subcollection *is* the
 *   dedup ledger: `enqueueEmails` reads it to drop recipients an earlier send
 *   already covered, and claims the survivors before dispatching. A row's
 *   existence is the claim; releasing one deletes it.
 *
 * The ledger is keyed by a hash of the normalized address rather than by uid,
 * because the admin send flow resolves contact lists (which mix raw emails,
 * uids, and nested list ids) down to a flat set of email addresses before it
 * ever reaches the server — the address is the only identifier every recipient
 * is guaranteed to have. `deriveEmailCampaignRecipientId` in
 * `@cip/email-queue` owns the derivation; this module only describes the
 * stored shape and the paths.
 *
 * @module
 */

import {z} from 'zod';

import type {UniqueStringId} from '../../types/branded';
import {FirestoreTimestampSchema} from '../types';

// ============================================================================
// Ids
// ============================================================================

/** Firestore document id of an `email-campaigns` document. */
export type EmailCampaignId = UniqueStringId<'EmailCampaignId'>;

// ============================================================================
// Campaign document
// ============================================================================

/**
 * Base schema for a campaign document. Uses {@link FirestoreTimestampSchema}
 * so the admin and client modules can each extend it with their own timestamp
 * transform.
 */
export const EmailCampaignDocumentSchema = z
  .object({
    /** Operator-facing campaign name, shown in the admin send and history UI. */
    name: z.string().min(1),
    /**
     * Course this campaign belongs to. Campaigns are top-level documents
     * rather than nested under the course so the recipient ledger can hang
     * off them directly; this field is what scopes them to a course.
     */
    courseId: z.string().min(1),
    /** When the campaign was created. */
    createdAt: FirestoreTimestampSchema,
    /**
     * When the campaign last changed — created, or a send attached to it.
     * Drives most-recent-first ordering in the campaign picker.
     */
    updatedAt: FirestoreTimestampSchema,
    /** Admin who created the campaign. */
    createdBy: z.object({
      /** Firebase Auth uid, taken from the callable's auth context. */
      uid: z.string(),
      /** Email of the creating admin, when the auth token carried one. */
      email: z.string().optional(),
    }),
    /**
     * Ids of every `EmailSendJob` enqueued into this campaign, in the order
     * they were sent. Bounded by the number of sends an operator makes for
     * one campaign, so an array is the right shape here — not a subcollection.
     */
    jobIds: z.array(z.string()),
    /**
     * Number of distinct addresses the campaign has claimed and not released.
     * A rollup of the `recipients` subcollection, maintained so the admin view
     * does not have to count the ledger.
     */
    recipientCount: z.number().int().nonnegative(),
  })
  .loose();

/** Inferred type for {@link EmailCampaignDocumentSchema}. */
export type EmailCampaignDocument = z.infer<typeof EmailCampaignDocumentSchema>;

// ============================================================================
// Recipient ledger document
// ============================================================================

/**
 * Base schema for one row of a campaign's dedup ledger.
 *
 * **The row's existence is the claim.** There is no released/active flag:
 * claiming an address is a Firestore `create`, which fails if a row is already
 * there, so two concurrent sends cannot both believe they own an address.
 * Releasing a claim — when delivery terminally fails and the recipient should
 * become eligible again — deletes the row. Encoding release as a field instead
 * would make every claim a precondition-guarded update for no gain: what the
 * campaign actually sent is already recorded on the `EmailSendJob` and the
 * `/mail` documents.
 */
export const EmailCampaignRecipientDocumentSchema = z
  .object({
    /**
     * The normalized (trimmed, lowercased) email address this row claims.
     * Stored alongside the hashed document id so the ledger stays readable.
     */
    email: z.string().min(1),
    /** Id of the `EmailSendJob` that claimed this address. */
    claimedByJobId: z.string(),
    /** When the address was claimed. */
    claimedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link EmailCampaignRecipientDocumentSchema}. */
export type EmailCampaignRecipientDocument = z.infer<
  typeof EmailCampaignRecipientDocumentSchema
>;

// ============================================================================
// Paths
// ============================================================================

/** Top-level collection holding campaign documents. */
export const EMAIL_CAMPAIGN_COLLECTION = 'email-campaigns' as const;

/** Subcollection under a campaign holding its recipient ledger. */
export const EMAIL_CAMPAIGN_RECIPIENTS_SUBCOLLECTION = 'recipients' as const;

/**
 * Path of the top-level campaign collection.
 *
 * @return The collection path `email-campaigns`.
 */
export function getEmailCampaignCollectionPath(): string {
  return EMAIL_CAMPAIGN_COLLECTION;
}

/**
 * Path of a single campaign document.
 *
 * @param params - Lookup parameters.
 * @param params.campaignId - Firestore id of the campaign.
 * @return The document path `email-campaigns/{campaignId}`.
 */
export function getEmailCampaignDocumentPath({
  campaignId,
}: {
  campaignId: string;
}): string {
  return `${EMAIL_CAMPAIGN_COLLECTION}/${campaignId}`;
}

/**
 * Path of a campaign's recipient ledger collection.
 *
 * @param params - Lookup parameters.
 * @param params.campaignId - Firestore id of the campaign.
 * @return The collection path `email-campaigns/{campaignId}/recipients`.
 */
export function getEmailCampaignRecipientCollectionPath({
  campaignId,
}: {
  campaignId: string;
}): string {
  return `${getEmailCampaignDocumentPath({campaignId})}/${EMAIL_CAMPAIGN_RECIPIENTS_SUBCOLLECTION}`;
}

/**
 * Path of one row of a campaign's recipient ledger.
 *
 * @param params - Lookup parameters.
 * @param params.campaignId - Firestore id of the campaign.
 * @param params.recipientId - Derived ledger id for the address (see
 *   `deriveEmailCampaignRecipientId` in `@cip/email-queue`). This is a hash,
 *   not the address itself.
 * @return The document path
 *   `email-campaigns/{campaignId}/recipients/{recipientId}`.
 */
export function getEmailCampaignRecipientDocumentPath({
  campaignId,
  recipientId,
}: {
  campaignId: string;
  recipientId: string;
}): string {
  return `${getEmailCampaignRecipientCollectionPath({campaignId})}/${recipientId}`;
}
